/**
 * A small area chart, drawn as inline SVG.
 *
 * No library: this needs a line, a fill and nothing else — no axes, no gridlines,
 * no legend, no tooltip. The value is written next to it in text, which is more
 * precise than any hover could be, so the drawing only has to answer "what shape
 * is this, and is it heading somewhere".
 *
 * Gaps are real. A null sample means the number could not be read, and the line
 * breaks rather than pretending the value held steady across it.
 */

const WIDTH = 100;
const HEIGHT = 28;

function toPoints(values, max) {
    const span = Math.max(values.length - 1, 1);
    const ceiling = max || 1;

    return values.map((value, index) => {
        if (value === null || value === undefined || !Number.isFinite(value)) return null;
        return {
            x: (index / span) * WIDTH,
            // SVG y grows downward, so a big value is a small y.
            y: HEIGHT - Math.min(value / ceiling, 1) * HEIGHT
        };
    });
}

/** Contiguous runs of real points, so a gap does not become a straight line across. */
function runs(points) {
    const out = [];
    let current = [];
    for (const point of points) {
        if (point) current.push(point);
        else if (current.length) {
            out.push(current);
            current = [];
        }
    }
    if (current.length) out.push(current);
    return out;
}

export function Sparkline({ values, max, className = "", tone = "text-blue-500" }) {
    const points = toPoints(values ?? [], max);
    const segments = runs(points);

    if (segments.every((segment) => segment.length < 2)) {
        return (
            <div className={`flex h-7 items-center ${className}`}>
                <span className="muted text-xs">collecting…</span>
            </div>
        );
    }

    return (
        <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            className={`h-7 w-full ${tone} ${className}`}
            aria-hidden
        >
            {segments.map((segment, index) => {
                if (segment.length < 2) return null;
                const line = segment.map((point, i) => `${i === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");
                const area = `${line} L${segment.at(-1).x.toFixed(2)},${HEIGHT} L${segment[0].x.toFixed(2)},${HEIGHT} Z`;
                return (
                    <g key={index}>
                        <path d={area} fill="currentColor" opacity="0.12" />
                        <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                    </g>
                );
            })}
        </svg>
    );
}
